package com.kinoshazam.screens.appInfo.compact

import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.LinearEasing
import androidx.compose.animation.core.tween
import androidx.compose.animation.core.withInfiniteAnimationFrameMillis
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.layout.wrapContentHeight
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableFloatStateOf
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.blur
import androidx.compose.ui.draw.clipToBounds
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.BlendMode
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.CompositingStrategy
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.TransformOrigin
import androidx.compose.ui.graphics.drawscope.DrawScope
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.layout.onSizeChanged
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.TextUnit
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.util.lerp
import com.kinoshazam.uiKit.theme.Medium
import com.kinoshazam.uiKit.theme.UiKitTheme
import com.kinoshazam.uiKit.theme.isUiKitInDarkTheme
import kotlinx.coroutines.delay
import kotlinx.coroutines.isActive
import kotlin.math.sin
import kotlin.math.roundToInt


private const val CrawlDurationMs = 32_000
private const val CrawlRestartDelayMs = 1_400L

@Composable
internal fun AuroraAboutPanel(
    title: String,
    body: String,
    modifier: Modifier = Modifier,
    contentTopPadding: Dp = 0.dp,
) {
    val textColor = if (isUiKitInDarkTheme()) Color.White else UiKitTheme.colors.color_1

    BoxWithConstraints(
        modifier = modifier
            .fillMaxSize()
            .background(UiKitTheme.colors.color_3),
    ) {
        val typography = remember(maxWidth, maxHeight) {
            auroraTypography(maxWidth, maxHeight)
        }

        GoatAurora(
            accent = UiKitTheme.colors.color_1,
            ice = UiKitTheme.colors.color_23,
            deep = UiKitTheme.colors.color_25,
            modifier = Modifier
                .fillMaxWidth()
                .fillMaxHeight(0.52f)
                .align(Alignment.BottomCenter),
        )

        Column(
            modifier = Modifier
                .align(Alignment.Center)
                .padding(top = contentTopPadding)
                .width(typography.columnWidth),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Text(
                text = title,
                color = textColor,
                style = Medium.copy(
                    fontSize = typography.titleSize,
                    lineHeight = typography.titleSize * 1.2f,
                    fontWeight = FontWeight.Bold,
                    letterSpacing = typography.titleTracking,
                ),
                textAlign = TextAlign.Center,
            )
            Spacer(modifier = Modifier.height(typography.gap))
            Text(
                text = body,
                modifier = Modifier.fillMaxWidth(),
                color = textColor,
                style = Medium.copy(
                    fontSize = typography.bodySize,
                    lineHeight = typography.bodySize * 1.35f,
                    fontWeight = FontWeight.Bold,
                    letterSpacing = typography.bodyTracking,
                ),
                textAlign = TextAlign.Center,
                softWrap = true,
            )
        }
    }
}

@Composable
internal fun CrawlAboutPanel(
    title: String,
    body: String,
    modifier: Modifier = Modifier,
) {
    val density = LocalDensity.current
    val surface = UiKitTheme.colors.color_3
    val textColor = if (isUiKitInDarkTheme()) Color.White else UiKitTheme.colors.color_1
    val progress = remember { Animatable(0f) }
    val textHeightPx = remember { mutableIntStateOf(0) }

    LaunchedEffect(title, body, textHeightPx.intValue) {
        if (textHeightPx.intValue == 0) return@LaunchedEffect
        val durationMs = (CrawlDurationMs + textHeightPx.intValue * 6)
            .coerceIn(CrawlDurationMs, 56_000)
        while (true) {
            progress.snapTo(0f)
            progress.animateTo(
                targetValue = 1f,
                animationSpec = tween(
                    durationMillis = durationMs,
                    easing = LinearEasing,
                ),
            )
            delay(CrawlRestartDelayMs)
        }
    }

    BoxWithConstraints(
        modifier = modifier
            .fillMaxSize()
            .background(surface)
            .clipToBounds(),
    ) {
        val typography = remember(maxWidth) { crawlTypography(maxWidth) }
        val viewport = constraints.maxHeight.toFloat()
        val startY = viewport * 0.62f
        val endY = -textHeightPx.intValue.toFloat() - viewport * 0.55f
        val translationY = lerp(startY, endY, progress.value)

        Box(
            modifier = Modifier
                .fillMaxSize()
                .graphicsLayer {
                    rotationX = 58f
                    cameraDistance = 10f * density.density
                    transformOrigin = TransformOrigin(0.5f, 0.92f)
                },
            contentAlignment = Alignment.TopCenter,
        ) {
            Column(
                modifier = Modifier
                    .widthIn(max = typography.columnWidth)
                    .fillMaxWidth()
                    .wrapContentHeight(unbounded = true)
                    .offset { IntOffset(0, translationY.roundToInt()) }
                    .onSizeChanged { textHeightPx.intValue = it.height },
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                Text(
                    text = title,
                    color = textColor,
                    style = Medium.copy(
                        fontSize = typography.titleSize,
                        lineHeight = typography.titleSize * 1.15f,
                        fontWeight = FontWeight.Bold,
                        letterSpacing = typography.titleTracking,
                    ),
                    textAlign = TextAlign.Center,
                    overflow = TextOverflow.Visible,
                )
                Spacer(modifier = Modifier.height(typography.gap))
                Text(
                    text = body,
                    modifier = Modifier.fillMaxWidth(),
                    color = textColor,
                    style = Medium.copy(
                        fontSize = typography.bodySize,
                        lineHeight = typography.bodySize * 1.45f,
                        fontWeight = FontWeight.Bold,
                        letterSpacing = typography.bodyTracking,
                    ),
                    textAlign = TextAlign.Justify,
                    overflow = TextOverflow.Visible,
                    softWrap = true,
                )
            }
        }
            CrawlVignette(
                color = surface,
                fromTop = true,
                modifier = Modifier.align(Alignment.TopCenter),
            )
            CrawlVignette(
                color = surface,
                fromTop = false,
                modifier = Modifier.align(Alignment.BottomCenter),
            )
    }
}

private data class CrawlTypography(
    val columnWidth: Dp,
    val titleSize: TextUnit,
    val bodySize: TextUnit,
    val titleTracking: TextUnit,
    val bodyTracking: TextUnit,
    val gap: Dp,
)

private fun auroraTypography(maxWidth: Dp, maxHeight: Dp): CrawlTypography {
    val columnWidth = (maxWidth * 0.42f).coerceIn(220.dp, 420.dp)
    val body = (maxHeight.value / 6f * 0.13f).coerceIn(13f, 18f)
    return CrawlTypography(
        columnWidth = columnWidth,
        titleSize = (body * 1.35f).sp,
        bodySize = body.sp,
        titleTracking = (body * 0.06f).sp,
        bodyTracking = 0.sp,
        gap = 10.dp,
    )
}

private fun crawlTypography(maxWidth: Dp): CrawlTypography {
    val columnWidth = (maxWidth * 0.96f).coerceAtLeast(280.dp)
    val body = (columnWidth.value * 0.094f).coerceIn(30f, 54f)
    return CrawlTypography(
        columnWidth = columnWidth,
        titleSize = (body * 1.7f).sp,
        bodySize = body.sp,
        titleTracking = (body * 0.12f).sp,
        bodyTracking = (body * 0.03f).sp,
        gap = (columnWidth.value * 0.055f).coerceIn(22f, 44f).dp,
    )
}

@Composable
private fun CrawlVignette(
    color: Color,
    fromTop: Boolean,
    modifier: Modifier = Modifier,
) {
    Box(
        modifier = modifier
            .fillMaxWidth()
            .height(if (fromTop) 96.dp else 72.dp)
            .background(
                Brush.verticalGradient(
                    colors = if (fromTop) {
                        listOf(color, color.copy(alpha = 0f))
                    } else {
                        listOf(color.copy(alpha = 0f), color)
                    },
                ),
            ),
    )
}

private data class AuroraCurtain(
    val x: Float,
    val width: Float,
    val height: Float,
    val teal: Boolean,
    val phase: Float,
    val speed: Float,
)

@Composable
private fun GoatAurora(
    accent: Color,
    ice: Color,
    deep: Color,
    modifier: Modifier = Modifier,
) {
    var timeSec by remember { mutableFloatStateOf(0f) }
    LaunchedEffect(Unit) {
        while (isActive) {
            withInfiniteAnimationFrameMillis { ms ->
                timeSec = ms / 1000f
            }
        }
    }

    val curtains = remember {
        listOf(
            AuroraCurtain(0.04f, 0.055f, 0.58f, teal = true, phase = 0.3f, speed = 0.41f),
            AuroraCurtain(0.09f, 0.07f, 0.72f, teal = false, phase = 0.4f, speed = 0.40f),
            AuroraCurtain(0.14f, 0.05f, 0.86f, teal = true, phase = 1.9f, speed = 0.48f),
            AuroraCurtain(0.19f, 0.08f, 0.90f, teal = false, phase = 0.2f, speed = 0.35f),
            AuroraCurtain(0.24f, 0.045f, 0.68f, teal = true, phase = 2.6f, speed = 0.53f),
            AuroraCurtain(0.29f, 0.06f, 1.00f, teal = false, phase = 1.1f, speed = 0.58f),
            AuroraCurtain(0.34f, 0.075f, 0.78f, teal = true, phase = 2.4f, speed = 0.42f),
            AuroraCurtain(0.39f, 0.05f, 0.94f, teal = false, phase = 0.8f, speed = 0.47f),
            AuroraCurtain(0.44f, 0.055f, 0.82f, teal = true, phase = 1.6f, speed = 0.50f),
            AuroraCurtain(0.49f, 0.09f, 1.08f, teal = false, phase = 0.7f, speed = 0.30f),
            AuroraCurtain(0.54f, 0.05f, 0.88f, teal = true, phase = 2.2f, speed = 0.45f),
            AuroraCurtain(0.59f, 0.055f, 0.96f, teal = false, phase = 3.2f, speed = 0.52f),
            AuroraCurtain(0.64f, 0.07f, 0.74f, teal = true, phase = 1.3f, speed = 0.39f),
            AuroraCurtain(0.69f, 0.08f, 0.84f, teal = false, phase = 1.8f, speed = 0.38f),
            AuroraCurtain(0.74f, 0.045f, 1.00f, teal = true, phase = 2.9f, speed = 0.46f),
            AuroraCurtain(0.79f, 0.06f, 0.70f, teal = false, phase = 0.5f, speed = 0.43f),
            AuroraCurtain(0.84f, 0.08f, 0.76f, teal = true, phase = 0.9f, speed = 0.36f),
            AuroraCurtain(0.89f, 0.05f, 0.92f, teal = false, phase = 2.7f, speed = 0.49f),
            AuroraCurtain(0.94f, 0.065f, 0.64f, teal = true, phase = 2.1f, speed = 0.44f),
        )
    }

    Canvas(
        modifier = modifier
            .graphicsLayer { compositingStrategy = CompositingStrategy.Offscreen }
            .blur(34.dp),
    ) {
        val w = size.width
        val h = size.height
        val t = timeSec * 1.75f
        val floorX = w * (0.5f + 0.06f * sin(t * 0.22f))
        val floor = Offset(floorX, h * 1.02f)
        val glowPulse = 0.86f + 0.14f * sin(t * 0.4f)

        drawRect(
            brush = Brush.verticalGradient(
                colors = listOf(
                    Color.Transparent,
                    deep.copy(alpha = 0.08f * glowPulse),
                    accent.copy(alpha = 0.10f * glowPulse),
                ),
            ),
        )

        drawCircle(
            brush = Brush.radialGradient(
                colors = listOf(
                    ice.copy(alpha = 0.28f * glowPulse),
                    accent.copy(alpha = 0.16f * glowPulse),
                    deep.copy(alpha = 0.08f),
                    Color.Transparent,
                ),
                center = floor,
                radius = w * (0.74f + 0.06f * sin(t * 0.18f)),
            ),
            center = floor,
            radius = w * (0.74f + 0.06f * sin(t * 0.18f)),
            blendMode = BlendMode.Plus,
        )

        drawCircle(
            brush = Brush.radialGradient(
                colors = listOf(
                    ice.copy(alpha = 0.22f * glowPulse),
                    Color.Transparent,
                ),
                center = Offset(floorX, h * 1.08f),
                radius = w * 0.34f,
            ),
            center = Offset(floorX, h * 1.08f),
            radius = w * 0.34f,
            blendMode = BlendMode.Plus,
        )

        curtains.forEach { curtain ->
            val wave = sin(t * curtain.speed + curtain.phase)
            val rise = sin(t * (curtain.speed * 0.7f) + curtain.phase * 1.3f)
            val shimmer = 0.72f + 0.28f * sin(t * (curtain.speed * 1.4f) + curtain.phase)
            drawGoatCurtain(
                centerX = w * (curtain.x + wave * 0.045f),
                width = w * curtain.width * (1f + 0.12f * wave),
                height = h * curtain.height * (0.78f + 0.22f * rise),
                color = if (curtain.teal) ice else accent,
                alpha = (if (curtain.width < 0.06f) 0.55f else 0.40f) * shimmer,
            )
        }

        drawRect(
            brush = Brush.verticalGradient(
                colors = listOf(Color.Transparent, Color.White),
            ),
            blendMode = BlendMode.DstIn,
        )
    }
}

private fun DrawScope.drawGoatCurtain(
    centerX: Float,
    width: Float,
    height: Float,
    color: Color,
    alpha: Float,
) {
    val tipY = size.height - height
    val baseY = size.height + height * 0.06f
    val baseHalf = width / 2f
    val tipHalf = baseHalf * 0.78f
    val path = Path().apply {
        moveTo(centerX - tipHalf, tipY)
        lineTo(centerX + tipHalf, tipY)
        quadraticTo(
            x1 = centerX + baseHalf,
            y1 = tipY + height * 0.55f,
            x2 = centerX + baseHalf,
            y2 = baseY,
        )
        lineTo(centerX - baseHalf, baseY)
        quadraticTo(
            x1 = centerX - baseHalf,
            y1 = tipY + height * 0.55f,
            x2 = centerX - tipHalf,
            y2 = tipY,
        )
        close()
    }
    drawPath(
        path = path,
        brush = Brush.verticalGradient(
            colors = listOf(
                Color.Transparent,
                color.copy(alpha = alpha * 0.25f),
                color.copy(alpha = alpha * 0.75f),
                color.copy(alpha = alpha),
            ),
            startY = tipY,
            endY = size.height,
        ),
        blendMode = BlendMode.Plus,
    )
}
